import React, { CSSProperties, useMemo, useState } from 'react'
import { List, Row, Tag } from 'antd'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { ApplicationState } from 'src/store'
import { ButtonTitled, Empty, Text, Tooltip } from 'src/components/Ant'
import { getVehiclesHistory } from 'src/core/store/ducks/vehicles/actions'
import { Request, RequestQuoteVehicle } from 'src/types'
import CurrencyMask from 'src/components/Formik/InputCurrency/mask'
import { TagTicketSituation } from 'src/components/molecules'
import Images from 'src/config/images'
import { WrapText } from './styles'
import ModalHistory from '../modal-history'
import { WrapCard } from '../../../../../styles'

type Props = {
  bodyStyle?: CSSProperties
  handleDelete?(item: SelectedVehicle): void
  request: Request
}

type SelectedVehicle = Pick<
  RequestQuoteVehicle,
  | 'model'
  | 'request_quote_vehicle_unique'
  | 'return_date'
  | 'return_location'
  | 'total_value'
  | 'vehicle_image_url'
  | 'withdrawal_date'
  | 'withdrawal_location'
> & {
  eticket_code?: string
  return_date_formatted: string
  situation?: any
  withdrawal_date_formatted: string
}

export default function SummaryCard({
  bodyStyle = {},
  handleDelete,
  request
}: Props) {
  const { vehicles } = useSelector((state: ApplicationState) => ({
    vehicles: state.vehicles
  }))

  const [visibleModalHistory, setVisibleModalHistory] = useState<boolean>(false)

  const dispatch = useDispatch()

  const selectedVehicles = useMemo<SelectedVehicle[]>(() => {
    const items: SelectedVehicle[] = []

    if (!request.request_id) {
      const selectedVehicles: SelectedVehicle[] = []

      request.full_request_vehicle.forEach(fullRequestVehicleItem => {
        const selectedVehicle = vehicles.list.find(
          findItem =>
            findItem.request_quote_vehicle_hash ===
            fullRequestVehicleItem.request_quote_vehicle_hash[0]
        )

        const selectedValue = selectedVehicle.values.find(
          findItem =>
            findItem.request_quote_vehicle_unique ===
            fullRequestVehicleItem.request_quote_vehicle_unique[0]
        )

        if (selectedValue) {
          selectedVehicles.push({
            model: selectedVehicle.model,
            total_value: selectedVehicle.total_value,
            vehicle_image_url: selectedVehicle.vehicle_image_url,
            return_location: selectedVehicle.return_location,
            withdrawal_location: selectedVehicle.withdrawal_location,
            request_quote_vehicle_unique:
              selectedValue.request_quote_vehicle_unique,
            return_date: selectedVehicle.return_date,
            return_date_formatted: moment
              .unix(selectedVehicle.return_date)
              .utc()
              .format('DD/MM/YYYY HH:mm'),
            withdrawal_date: selectedVehicle.withdrawal_date,
            withdrawal_date_formatted: moment
              .unix(selectedVehicle.withdrawal_date)
              .utc()
              .format('DD/MM/YYYY HH:mm')
          })
        }
      })

      return selectedVehicles
    }
    request.request_tickets
      .filter(
        requestTicketItem =>
          requestTicketItem.class_type === 'SERVICE' &&
          requestTicketItem.request_ticket_type === 'VEHICLE'
      )
      .forEach(requestTicketItem => {
        items.push({
          model:
            requestTicketItem.request_ticket_vehicle_selected
              .request_quote_vehicle.model,
          total_value:
            requestTicketItem.request_ticket_vehicle_selected
              .request_quote_vehicle.total_value,
          vehicle_image_url:
            requestTicketItem.request_ticket_vehicle_selected
              .request_quote_vehicle.vehicle_image_url,
          return_location:
            requestTicketItem.request_ticket_vehicle_selected
              .request_quote_vehicle.return_location,
          withdrawal_location:
            requestTicketItem.request_ticket_vehicle_selected
              .request_quote_vehicle.withdrawal_location,
          request_quote_vehicle_unique:
            requestTicketItem.request_ticket_vehicle_selected
              .request_quote_vehicle.request_quote_vehicle_unique,
          return_date:
            requestTicketItem.request_ticket_vehicle_selected
              .request_quote_vehicle.return_date,
          return_date_formatted: moment
            .unix(
              requestTicketItem.request_ticket_vehicle_selected
                .request_quote_vehicle.return_date
            )
            .utc()
            .format('DD/MM/YYYY HH:mm'),
          withdrawal_date:
            requestTicketItem.request_ticket_vehicle_selected
              .request_quote_vehicle.withdrawal_date,
          withdrawal_date_formatted: moment
            .unix(
              requestTicketItem.request_ticket_vehicle_selected
                .request_quote_vehicle.withdrawal_date
            )
            .utc()
            .format('DD/MM/YYYY HH:mm'),
          situation: requestTicketItem.status,
          eticket_code:
            requestTicketItem.request_ticket_vehicle_selected.eticket_code
        })
      })

    return items
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.full_request_vehicle, request.request_tickets])

  const hasHistory = useMemo<boolean>(() => {
    return (
      typeof request?.request_id === 'number' && selectedVehicles?.length >= 1
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.request_id, selectedVehicles])

  return (
    <>
      <WrapCard
        bodyStyle={bodyStyle}
        title="web.services.vehicle.list-title"
        extra={[
          hasHistory && (
            <ButtonTitled
              key="add-vehicle-history"
              icon="history"
              onClick={() => {
                if (request?.request_id) {
                  dispatch(
                    getVehiclesHistory({ request_id: request.request_id })
                  )
                }
                setVisibleModalHistory(true)
              }}
              size="default"
              tooltip="common.actions.historical"
            />
          )
        ]}
      >
        <List
          data-test-id="vehicle-summary-list"
          itemLayout="horizontal"
          dataSource={selectedVehicles}
          locale={{ emptyText: <Empty onlyText /> }}
          renderItem={(item, index) => (
            <List.Item
              data-test-id={`vehicle-summary-item-${index}`}
              actions={[
                ...(handleDelete
                  ? [
                      <ButtonTitled
                        data-test-id={`vehicle-summary-remove-${index}`}
                        icon="delete"
                        onClick={() => handleDelete(item)}
                        shape="circle"
                        tooltip="common.actions.remove"
                        type="danger"
                      />
                    ]
                  : [])
              ]}
            >
              <List.Item.Meta
                avatar={
                  <div
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      width: 70
                    }}
                  >
                    <img
                      alt="logo"
                      src={
                        item.vehicle_image_url
                          ? item.vehicle_image_url
                          : Images.noImage
                      }
                      style={{ maxHeight: 70, maxWidth: 70 }}
                    />
                  </div>
                }
                title={
                  <Tooltip title={<>{item.model}</>}>
                    <WrapText>
                      <Text strong>{item.model}</Text>
                    </WrapText>
                  </Tooltip>
                }
                description={
                  <>
                    {!!item.situation && (
                      <TagTicketSituation status={item.situation} />
                    )}
                    {!!request.request_id && (
                      <Row>
                        {!!item.eticket_code && (
                          <>
                            <Text
                              colon
                              strong
                              title="web.services.vehicle.form.emission"
                            />{' '}
                            <Text>{item.eticket_code}</Text>
                          </>
                        )}
                      </Row>
                    )}
                    <Row>
                      <Text
                        colon
                        strong
                        title="web.services.vehicle.form.withdrawal.title"
                      />
                      <Text>{item.withdrawal_date_formatted}</Text>
                    </Row>
                    <Row>
                      <Text
                        colon
                        strong
                        title="web.services.vehicle.form.devolution.title"
                      />
                      <Text>{item.return_date_formatted}</Text>
                    </Row>
                    <Row>
                      <Tag
                        color="green"
                        style={{
                          fontSize: 20,
                          padding: 4,
                          textAlign: 'center'
                        }}
                      >
                        {`BRL ${
                          CurrencyMask({
                            formatStr: true,
                            value: item.total_value
                          }).maskedValue
                        }`}
                      </Tag>
                    </Row>
                  </>
                }
              />
            </List.Item>
          )}
        />
      </WrapCard>
      <ModalHistory
        toggle={() => {
          setVisibleModalHistory(false)
        }}
        visible={visibleModalHistory}
      />
    </>
  )
}
